import { useCallback, useMemo, useState } from 'react'

// 범용 "테이블 다중선택" 훅. 특정 목록에 의존하지 않는다(제네릭 Id).
// 선택은 Set으로 보관해 페이지 이동·필터 변경에도 유지된다.
// allState/toggleAll은 "현재 보이는 행(visibleIds)" 기준 — 전체선택 체크박스의 indeterminate에 쓴다.

export type SelectionState = 'none' | 'some' | 'all'

export interface TableSelection<Id> {
    /** 선택된 id 집합(페이지 이동에도 유지). */
    selected: Set<Id>
    /** 선택 개수(파생). */
    count: number
    isSelected: (id: Id) => boolean
    /** 단건 토글. */
    toggle: (id: Id) => void
    /** 여러 id를 한 번에 선택에 추가(이미 선택된 건 유지). */
    selectMany: (ids: Id[]) => void
    /** 전체 해제. */
    clear: () => void
    /** visibleIds 기준 선택 상태(none=0개, all=전부, some=일부). */
    allState: (visibleIds: Id[]) => SelectionState
    /** visibleIds 기준 전체 토글 — 전부 선택돼 있으면 그 범위만 해제, 아니면 전부 선택. */
    toggleAll: (visibleIds: Id[]) => void
}

/** 테이블 행 다중선택 상태를 관리한다. Id 기본형은 string. */
export function useTableSelection<Id = string>(): TableSelection<Id> {
    const [selected, setSelected] = useState<Set<Id>>(() => new Set())

    const isSelected = useCallback((id: Id) => selected.has(id), [selected])

    const toggle = useCallback((id: Id) => {
        setSelected((prev) => {
            const next = new Set(prev)
            if (next.has(id)) next.delete(id)
            else next.add(id)
            return next
        })
    }, [])

    const selectMany = useCallback((ids: Id[]) => {
        setSelected((prev) => {
            const next = new Set(prev)
            for (const id of ids) next.add(id)
            return next
        })
    }, [])

    const clear = useCallback(() => setSelected(new Set()), [])

    const allState = useCallback(
        (visibleIds: Id[]): SelectionState => {
            if (visibleIds.length === 0) return 'none'
            let hit = 0
            for (const id of visibleIds) if (selected.has(id)) hit++
            if (hit === 0) return 'none'
            return hit === visibleIds.length ? 'all' : 'some'
        },
        [selected]
    )

    const toggleAll = useCallback((visibleIds: Id[]) => {
        setSelected((prev) => {
            const allChecked = visibleIds.every((id) => prev.has(id))
            const next = new Set(prev)
            if (allChecked) for (const id of visibleIds) next.delete(id)
            else for (const id of visibleIds) next.add(id)
            return next
        })
    }, [])

    const count = selected.size

    return useMemo(
        () => ({
            selected,
            count,
            isSelected,
            toggle,
            selectMany,
            clear,
            allState,
            toggleAll,
        }),
        [
            selected,
            count,
            isSelected,
            toggle,
            selectMany,
            clear,
            allState,
            toggleAll,
        ]
    )
}
